// Destination search for Malta.
// Priority: Google Places (only when a key is configured) → Photon → Nominatim.
// The OSM sources always stay available, so search never dies with Google.

import {
  googleAutocomplete,
  googleTextSearch,
  googlePlaceDetails,
  googlePlacesEnabled,
  endSession,
} from "./googlePlaces";

const PHOTON = "https://photon.komoot.io/api";
const MALTA_BBOX = "13.95,35.62,14.88,36.22"; // minLon,minLat,maxLon,maxLat
const BIAS = { lat: 35.9375, lng: 14.3754 };
const TIMEOUT_MS = 4500;

const POI_KEYS = new Set([
  "amenity",
  "shop",
  "healthcare",
  "office",
  "leisure",
  "tourism",
  "craft",
  "club",
  "emergency",
]);
const PLACE_TYPES = new Set(["city", "town", "village", "locality", "district", "suburb", "hamlet"]);

const inMalta = (lat, lng) => lat > 35.62 && lat < 36.22 && lng > 13.95 && lng < 14.88;

/** Lowercase, strip diacritics, unify the apostrophes used in Maltese names. */
const norm = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’‘`´]/g, "'")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const hasDigit = (s) => /\d/.test(s || "");

function nameScore(name, q) {
  const n = norm(name);
  if (!n) return 0;
  if (n === q) return 100;
  if (n.startsWith(q)) return 72;
  if (n.split(/[\s,.-]+/).some((w) => w.startsWith(q))) return 56;
  if (n.includes(q)) return 40;
  return 8;
}

function score(r, q) {
  let s = nameScore(r.name, q);
  if (r.is_poi && !hasDigit(q)) s += 18;
  if (r.is_place && norm(r.name) === q) s += 26;
  if (hasDigit(q) && r.has_housenumber) s += 30;
  if (r.street && norm(r.street).includes(q)) s += 6;
  return s;
}

async function fetchJson(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, { ...options, signal: options.signal || controller.signal });
    if (!res.ok) throw new Error(`http_${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

function photonLabel(p) {
  return [p.housenumber && p.street ? `${p.street} ${p.housenumber}` : p.street, p.city || p.district || p.county, p.postcode]
    .filter(Boolean)
    .join(", ");
}

async function photonSearch(q, anchor, signal) {
  const params = new URLSearchParams({
    q,
    limit: "10",
    lang: "en",
    lat: String(anchor.lat),
    lon: String(anchor.lng),
    bbox: MALTA_BBOX,
  });
  const data = await fetchJson(`${PHOTON}?${params}`, { signal });
  const out = [];
  for (const f of data.features || []) {
    const [lng, lat] = f.geometry?.coordinates || [];
    const p = f.properties || {};
    const name = p.name || p.street || p.city;
    if (!name || !Number.isFinite(lat) || !inMalta(lat, lng)) continue;
    out.push({
      id: `p-${p.osm_type || "x"}${p.osm_id || out.length}`,
      name,
      label: photonLabel(p),
      lat,
      lng,
      street: p.street || null,
      is_poi: POI_KEYS.has(p.osm_key),
      is_place: p.osm_key === "place" || PLACE_TYPES.has(p.type),
      has_housenumber: Boolean(p.housenumber),
    });
  }
  return out;
}


function merge(lists, q) {
  const seen = new Map();
  for (const r of lists.flat()) {
    const key = `${norm(r.name)}@${r.lat.toFixed(4)},${r.lng.toFixed(4)}`;
    const scored = { ...r, _score: score(r, q) };
    const prev = seen.get(key);
    if (!prev || prev._score < scored._score) seen.set(key, scored);
  }
  return [...seen.values()].sort((a, b) => b._score - a._score).slice(0, 6);
}

/** `[{id, name, label, lat?, lng?, needs_details?}]` — at most 6 Malta results. */
export async function searchDestinations(query, { bias, signal } = {}) {
  const raw = (query || "").trim();
  if (raw.length < 3) return [];
  const q = norm(raw);
  const anchor = bias && inMalta(bias.lat, bias.lng) ? bias : BIAS;

  if (googlePlacesEnabled()) {
    try {
      const google = await googleAutocomplete(raw, signal);
      if (google?.length) return google.slice(0, 6);
      // Descriptive queries can miss autocomplete entirely; one text search
      // rescues them before we drop to the OSM sources.
      const text = await googleTextSearch(raw, signal);
      if (text?.length) return text.slice(0, 6);
    } catch {
      /* fall through to the OSM sources */
    }
  }

  let photon = [];
  try {
    photon = await photonSearch(raw, anchor, signal);
  } catch {
    photon = [];
  }
  // Photon only: Nominatim forbids automated autocomplete querying, so it is
  // never called from the type-ahead path.
  return merge([photon], q);
}

/** Google predictions carry no coordinates until the driver taps one. */
export async function resolveDestination(result) {
  if (!result?.needs_details) {
    endSession();
    return result;
  }
  const detailed = await googlePlaceDetails(result.place_id);
  return detailed || null;
}
